import { AbstractControl } from '@angular/forms';

export interface ParsedEmailPaste {
    email: string;
    firstName?: string;
    lastName?: string;
}

export interface EmailPasteNameTargets {
    name?: AbstractControl | null;
    firstName?: AbstractControl | null;
    lastName?: AbstractControl | null;
}

export interface EmailPasteOptions {
    fillOnlyWhenEmpty?: boolean;
}

export function parsePastedEmailInput(pastedText: string): ParsedEmailPaste | null {
    const trimmed = pastedText?.trim();
    if (!trimmed) {
        return null;
    }

    let email = trimmed;
    const hasMailtoPrefix = email.toLowerCase().startsWith('mailto:');

    if (hasMailtoPrefix) {
        email = email.slice(7);
        const queryIndex = email.indexOf('?');
        if (queryIndex !== -1) {
            email = email.substring(0, queryIndex);
        }
        email = email.trim();
    }

    const nameParts = extractNamePartsFromEmail(email);
    if (!hasMailtoPrefix && !nameParts) {
        return null;
    }

    return {
        email,
        ...nameParts,
    };
}

export function handleEmailPasteEvent(
    event: ClipboardEvent,
    emailControl: AbstractControl,
    nameTargets?: EmailPasteNameTargets,
    options: EmailPasteOptions = { fillOnlyWhenEmpty: true }
): boolean {
    const pastedText = event.clipboardData?.getData('text') ?? '';
    const parsed = parsePastedEmailInput(pastedText);
    if (!parsed) {
        return false;
    }

    event.preventDefault();
    emailControl.patchValue(parsed.email);
    applyParsedNameToControls(parsed, nameTargets, options);

    return true;
}

function extractNamePartsFromEmail(email: string): Pick<ParsedEmailPaste, 'firstName' | 'lastName'> | null {
    const fullNameMatch = email.match(/^([a-zA-Z]+)\.([a-zA-Z]+)@.+$/);
    if (fullNameMatch) {
        return {
            firstName: capitalizeNamePart(fullNameMatch[1]),
            lastName: capitalizeNamePart(fullNameMatch[2]),
        };
    }

    const singleNameMatch = email.match(/^([a-zA-Z]+)@.+$/);
    if (singleNameMatch) {
        return {
            firstName: capitalizeNamePart(singleNameMatch[1]),
        };
    }

    return null;
}

function applyParsedNameToControls(
    parsed: ParsedEmailPaste,
    nameTargets: EmailPasteNameTargets | undefined,
    options: EmailPasteOptions
): void {
    if (!nameTargets || !parsed.firstName) {
        return;
    }

    const fillOnlyWhenEmpty = options.fillOnlyWhenEmpty ?? true;
    const shouldFill = (control: AbstractControl | null | undefined): control is AbstractControl =>
        !!control && (!fillOnlyWhenEmpty || !control.value);

    if (shouldFill(nameTargets.name)) {
        const fullName = parsed.lastName ? `${parsed.firstName} ${parsed.lastName}` : parsed.firstName;
        nameTargets.name.patchValue(fullName);
    }

    if (shouldFill(nameTargets.firstName)) {
        nameTargets.firstName.patchValue(parsed.firstName);
    }

    if (parsed.lastName && shouldFill(nameTargets.lastName)) {
        nameTargets.lastName.patchValue(parsed.lastName);
    }
}

function capitalizeNamePart(namePart: string): string {
    return namePart.charAt(0).toUpperCase() + namePart.slice(1).toLowerCase();
}
